import { createContext, useCallback, useContext, useMemo } from 'react';
import { useFetcher } from '@remix-run/react';
import type { loader as rootLoader } from '~/routes/_index.tsx';

type SearchFetcherData = ReturnType<
  typeof useFetcher<typeof rootLoader>
>['data'];

type SearchContextType = {
  searchFetcher: ReturnType<typeof useFetcher<typeof rootLoader>>;
  triggerSearch: (query: string, filterTag?: string | null) => void;
  resetSearch: () => void;
  fetcherData: SearchFetcherData;
  isSearching: boolean;
};

const SearchContext = createContext<SearchContextType | null>(null);

type SearchProviderProps = {
  children: React.ReactNode;
};

/**
 * Provider component that manages client-side search state.
 * Wraps children with SearchContext and provides fetcher-based search functionality.
 */
export function SearchProvider({ children }: SearchProviderProps): JSX.Element {
  const searchFetcher = useFetcher<typeof rootLoader>();

  const triggerSearch = useCallback(
    (query: string, filterTag?: string | null): void => {
      const url = new URL(window.location.href);
      if (query) {
        url.searchParams.set('q', query);
      } else {
        url.searchParams.delete('q');
      }
      if (filterTag != null && filterTag !== '') {
        url.searchParams.set('filterTag', filterTag);
      } else {
        url.searchParams.delete('filterTag');
      }
      window.history.replaceState(null, '', url.toString());
      const fetchUrl = new URL(
        window.location.pathname,
        window.location.origin
      );
      if (query) {
        fetchUrl.searchParams.set('q', query);
      }
      if (filterTag != null && filterTag !== '') {
        fetchUrl.searchParams.set('filterTag', filterTag);
      }
      fetchUrl.searchParams.set('index', '');
      const fetchPathWithQuery: string = `${fetchUrl.pathname}${fetchUrl.search}`;
      searchFetcher.load(fetchPathWithQuery);
    },
    [searchFetcher]
  );

  const resetSearch = useCallback((): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete('q');
    url.searchParams.delete('filterTag');
    window.history.replaceState(null, '', url.toString());
    const fetchUrl = `${window.location.pathname}?index`;
    searchFetcher.load(fetchUrl);
  }, [searchFetcher]);

  const value = useMemo(
    () => ({
      searchFetcher,
      triggerSearch,
      resetSearch,
      fetcherData: searchFetcher.data,
      isSearching: searchFetcher.state === 'loading',
    }),
    [
      searchFetcher,
      searchFetcher.data,
      searchFetcher.state,
      triggerSearch,
      resetSearch,
    ]
  );

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
}

/**
 * Hook to access the search context for client-side search functionality.
 * Provides access to the search fetcher and trigger function.
 * @returns SearchContextType with fetcher and triggerSearch function
 * @throws Error if used outside of SearchProvider
 */
export function useSearchContext(): SearchContextType {
  const context = useContext(SearchContext);

  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }

  return context;
}
